import type {ReactNode} from 'react'
import {CommentDiscussionIcon, TriangleDownIcon} from '@primer/octicons-react'
import {ActionList, ActionMenu, Button} from '@primer/react'
import {getCurrentUserLogin} from '@github-ui/client-env'
import {useRouteParams} from '@github-ui/react-core/future/use-route-params'
import {Link} from '@github-ui/react-router'

import {ForYouItem} from '../shared/ForYouItem'
import {ForYouLeadingIcon} from '../shared/ForYouLeadingIcon'
import {usePullRequestAnalytics} from '../../../../hooks/use-pull-request-analytics'
import {pullRequestsLayoutRoute} from '../../../../routes/layout-route'
import {pullRequestsChangesRoute} from '../../../../routes/files-changed-routes'
import {DelegatedFromTeams} from './DelegatedFromTeams'
import {useReviewRequestedData, type ReviewRequestedData} from './use-review-requested-data'

const ITEM_NAME = 'review-requested'

export function shouldShowReviewRequested(data: ReviewRequestedData | null) {
  return data !== null
}

// Surfaces the viewer's requested-review state in the "Needs attention" section, with a
// shortcut to start reviewing on the Files changed page.
export function ReviewRequested({variant}: {variant?: 'default' | 'embedded'}) {
  const data = useReviewRequestedData()
  const {owner, repo, pr_number} = useRouteParams(pullRequestsLayoutRoute)
  if (!shouldShowReviewRequested(data)) {
    return null
  }

  const changesPath = pullRequestsChangesRoute.generatePath({owner, repo, pr_number})

  const {title, description} = messageFor(data)

  return (
    <ForYouItem
      leadingVisual={<ForYouLeadingIcon icon={CommentDiscussionIcon} variant="warning" />}
      title={title}
      itemName={ITEM_NAME}
      variant={variant}
      description={description}
      actionSlot={<ReviewRequestedActions data={data} changesPath={changesPath} />}
    />
  )
}

function messageFor(data: ReviewRequestedData): {title: string; description?: ReactNode} {
  switch (data.kind) {
    case 'delegated': {
      return {
        title: 'You have been assigned to review',
        description:
          data.delegatedFromTeams.length > 0 ? <DelegatedFromTeams teams={data.delegatedFromTeams} /> : undefined,
      }
    }
    case 'codeowner':
      return {title: 'Your review has been requested as a code owner'}
    case 'direct':
      return {title: 'Your review has been requested'}
  }
}

function ReviewRequestedActions({data, changesPath}: {data: ReviewRequestedData; changesPath: string}) {
  const {sendPullRequestAnalyticsEvent} = usePullRequestAnalytics()

  const sendMenuAction = (actionName: string) => {
    sendPullRequestAnalyticsEvent('overview.for_you_item_action', 'OVERVIEW_FOR_YOU_ITEM', {
      item_name: ITEM_NAME,
      action_name: actionName,
    })
  }

  // Code owner: offer the full review plus a shortcut filtered to the viewer's own files. Use a
  // single-width menu button rather than two side-by-side buttons so the action always fits inside
  // the fixed-width trailing column and never clips at 320px / 400% zoom (WCAG 1.4.10 Reflow).
  if (data.kind === 'codeowner') {
    const viewerLogin = getCurrentUserLogin()
    const ownedFilesPath = viewerLogin ? `${changesPath}?owned-by[]=${encodeURIComponent(viewerLogin)}` : changesPath

    return (
      <ActionMenu>
        <ActionMenu.Button
          size="small"
          trailingAction={TriangleDownIcon}
          onClick={() =>
            sendPullRequestAnalyticsEvent('overview.for_you_item_action', 'OVERVIEW_FOR_YOU_ITEM', {
              item_name: ITEM_NAME,
              action_name: 'open_review_menu',
            })
          }
        >
          Review
        </ActionMenu.Button>
        <ActionMenu.Overlay align="end">
          <ActionList>
            <ActionList.LinkItem as={Link} to={changesPath} onClick={() => sendMenuAction('review_all_files')}>
              Review all files
            </ActionList.LinkItem>
            <ActionList.LinkItem as={Link} to={ownedFilesPath} onClick={() => sendMenuAction('review_owned_files')}>
              Review just your files
            </ActionList.LinkItem>
          </ActionList>
        </ActionMenu.Overlay>
      </ActionMenu>
    )
  }

  return (
    <Button as={Link} to={changesPath} size="small" onClick={() => sendMenuAction('review')}>
      Review
    </Button>
  )
}
